/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useMemo, useState } from 'react';

import axios from 'axios';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';

import SplitTemplate from '../../components/SplitTemplate';
import UnderlinedInput from '../../components/Inputs/UnderlinedInput';
import ConfirmButton from '../../components/Buttons/ConfirmButton';
import { SocketContext } from '../../context';
import { UiContext } from '../../context/UiContext';
import { saveAuthData } from '../../helpers/authStorage';
import { USER_ROLES } from '../../constants/main';
import logo from '../../assets/images/snapsop2.png';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import classes from './styles.module.scss';

const validationSchema = yup.object({
  password: yup
    .string()
    .required('This field is required')
    .min(8, 'The password should have at minimum length of 8'),
  confirmPassword: yup
    .string()
    .required('This field is required')
    .oneOf([yup.ref('password'), null], 'Passwords do not match'),
});

export default function ChangePasswordPage() {
  const [currentView, setCurrentView] = useState('change');

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const invitationType = searchParams.get('type');
  const inviteeNameParam = searchParams.get('inviteeName');
  const inviteeName = inviteeNameParam?.trim() || 'there';
  const isNewUserInvitation = invitationType === 'invitation';

  const navigate = useNavigate();

  const { notifySuccess, redirectAfterLogin, setRedirectAfterLogin } =
    useContext(UiContext);
  const { socket } = useContext(SocketContext);
  const isMobile = useCheckMobileScreen();

  const changePassword = async ({ password }) => {
    try {
      const response = await axios.post('/change-password', {
        token,
        password,
      });

      saveAuthData(response.data);
      if (socket) {
        socket.auth.token = response.data.accessToken;
        socket.disconnect().connect();
      }

      notifySuccess(response.data.message || 'Password changed successfully');

      const isSuperAdmin = response.data?.role === USER_ROLES.SUPER_ADMIN;
      const isDefaultPage = ['/my-videos', '/my-library'].includes(
        redirectAfterLogin
      );
      let finalRedirect = redirectAfterLogin;

      if (!finalRedirect || isDefaultPage) {
        if (isSuperAdmin) {
          finalRedirect = '/super-admin';
        } else if (isMobile) {
          finalRedirect = '/my-videos';
        } else {
          finalRedirect = '/my-library';
        }
      }

      navigate(finalRedirect);

      if (redirectAfterLogin) {
        setRedirectAfterLogin('');
      }
    } catch (error) {
      console.error(error);
      setCurrentView('expired');
    }
  };

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: changePassword,
  });

  const hasError = useMemo(
    () => Object.values(formik.errors).some((error) => error),
    [formik.errors]
  );
  const hasEmptyValue = useMemo(
    () => Object.values(formik.values).some((value) => !value.length),
    [formik.values]
  );

  const isSubmitDisabled = hasError || hasEmptyValue;

  return (
    <div className={classes.ChangePasswordPage}>
      <SplitTemplate>
        {currentView === 'change' && (
          <>
            {isNewUserInvitation && (
              <section className={classes.welcomeMessage}>
                <div className={classes.welcomeHeader}>
                  <div>
                    <span className={classes.inviteBadge}>Team invitation</span>
                <p className={classes.greeting}>
                  Hi, {inviteeName}
                </p>
                    <h2>Welcome to Snap SOP</h2>
                  </div>
                  <div className={classes.logoBadge}>
                <img src={logo} alt="Snap SOP" />
                  </div>
                </div>
                <p className={classes.welcomeDescription}>
                  You&apos;re about to join your workspace and start contributing SOPs. Create a secure
                  password so you can document, review, and share process videos with the rest of the
                  team.
                </p>
                <ul className={classes.welcomeHighlights}>
                  <li>
                    <strong>Document quickly</strong>
                    <span>Capture SOPs with video, steps, and references in one place.</span>
                  </li>
                  <li>
                    <strong>Collaborate clearly</strong>
                    <span>Assign owners and keep teammates in sync with role-based access.</span>
                  </li>
                  <li>
                    <strong>Stay current</strong>
                    <span>Track completion so every runbook stays accurate for the team.</span>
                  </li>
                </ul>
              </section>
            )}
            <h1>Create new password</h1>
            <form onSubmit={formik.handleSubmit}>
              <UnderlinedInput
                label={
                  formik.errors.password && formik.touched.password
                    ? formik.errors.password
                    : 'New password'
                }
                value={formik.values.password}
                errors={formik.errors.password}
                isTouched={formik.touched.password}
                type="password"
                name="password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isPassword
                doPasswordsMatch={
                  formik.errors.confirmPassword !== 'Passwords do not match'
                }
              />

              <UnderlinedInput
                label={
                  formik.errors.confirmPassword === 'This field is required' &&
                  formik.touched.confirmPassword
                    ? 'This field is required'
                    : 'Repeat password'
                }
                value={formik.values.confirmPassword}
                errors={
                  formik.errors.confirmPassword === 'This field is required'
                }
                isTouched={formik.touched.confirmPassword}
                type="password"
                name="confirmPassword"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <ConfirmButton isDisabled={isSubmitDisabled} />
            </form>
          </>
        )}

        {currentView === 'expired' && (
          <>
            <span className={classes.resetPassword}>
              <Link to="/forgot-password">Reset password</Link>
            </span>
            <h1>The link has expired</h1>
            <p>
              Seems the reset password link has expired. No worries though, just
              try to reset the password again.
            </p>
          </>
        )}
      </SplitTemplate>
    </div>
  );
}
